import React, { useState, useRef, useEffect } from "react";
import { Grid, Box } from "@mui/material";
import SignUpStepLayout from "./SignUpStepLayout";
import TwoFactorCodeInput from "../forms/TwoFactorCodeInput";
import { saveStepData, StepData } from "./signUpApi";

interface SignUpStep2Props {
  onNextStep: () => void;
}

const SignUpStep2: React.FC<SignUpStep2Props> = ({ onNextStep }) => {
  const [code, setCode] = useState(new Array(6).fill(""));
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [whisper] = useState<React.ReactNode>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const firstCodeInputRef = useRef<HTMLInputElement>(null); // Reference to the first input of the TwoFactorCodeInput

  useEffect(() => {
    if (code.every((num) => num !== "") && !isVerifying) {
      setIsVerifying(true);
      const verifyCode = async () => {
        try {
          const formattedCode = code.join("");
          const email = localStorage.getItem("accountEmail");
          const data: StepData = {
            code: formattedCode,
            email: email === null ? "" : email,
          };

          const result = await saveStepData("4", data);

          if (result.success) {
            onNextStep();
          } else {
            // Show error message and clear the code
            setSnackbarMessage(
              result.error || "Invalid verification code. Please try again.",
            );
            setCode(new Array(6).fill(""));
            // Focus back to first input
            setTimeout(() => {
              if (firstCodeInputRef.current) {
                firstCodeInputRef.current.focus();
              }
            }, 100);
          }
        } catch (error) {
          console.error("Verification error:", error);
          setSnackbarMessage("An error occurred. Please try again.");
          setCode(new Array(6).fill(""));
          setTimeout(() => {
            if (firstCodeInputRef.current) {
              firstCodeInputRef.current.focus();
            }
          }, 100);
        } finally {
          setIsVerifying(false);
        }
      };

      verifyCode();
    }
  }, [code, isVerifying, onNextStep]);

  // Clear snackbar message after showing it
  useEffect(() => {
    if (snackbarMessage) {
      const timer = setTimeout(() => {
        setSnackbarMessage("");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [snackbarMessage]);

  return (
    <SignUpStepLayout
      title="Please enter the 6-character code here."
      whisper={whisper}
      snackbarMessage={snackbarMessage}
      snackbarSeverity="error"
    >
      <Box
        component="form"
        onSubmit={(e) => e.preventDefault()}
        sx={{ width: "100%" }}
      >
        <Grid container spacing={2} justifyContent="center">
          <TwoFactorCodeInput
            code={code}
            onCodeChange={setCode}
            firstCodeInputRef={firstCodeInputRef}
          />
        </Grid>
      </Box>
    </SignUpStepLayout>
  );
};

export default SignUpStep2;
